import { z } from 'zod';

import { actorSchema } from './clinical-common.ts';

// Patient feedback (docs/plans/phase3-portal-feedback-kiswahili.md §5). The comment is the
// patient's own words and may hold health details: it is patient data, shown only on the
// staff detail view (which is audited) and never logged.

export const FEEDBACK_RATINGS = [1, 2, 3, 4, 5] as const;
export const feedbackRatingSchema = z.number().int().min(1).max(5);
export const FEEDBACK_COMMENT_MAX = 1000;
export const feedbackCommentSchema = z.string().trim().max(FEEDBACK_COMMENT_MAX);

export const FEEDBACK_SOURCES = ['portal', 'sms_link'] as const;
export const FEEDBACK_STATUSES = ['open', 'resolved'] as const;

export const submitFeedbackBodySchema = z.object({
  rating: feedbackRatingSchema,
  comment: feedbackCommentSchema.optional(),
});
export type SubmitFeedbackBody = z.infer<typeof submitFeedbackBodySchema>;

/** The SMS link token: 24 random bytes, base64url. Posted in the body, never in an API URL. */
export const feedbackLinkTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{32}$/);

export const feedbackLinkBodySchema = z.object({ token: feedbackLinkTokenSchema });

/** What the link page may show once the token is checked: nothing that names the patient. */
export const feedbackLinkSchema = z.object({
  facilityName: z.string(),
  visitDate: z.iso.date(),
});
export type FeedbackLinkView = z.infer<typeof feedbackLinkSchema>;

export const feedbackLinkSubmitBodySchema = submitFeedbackBodySchema.extend({
  token: feedbackLinkTokenSchema,
});

// --- In the portal -------------------------------------------------------------------------

export const portalFeedbackSchema = z.object({
  /** Closed visits of the last 30 days without feedback yet (plan §5.1). */
  waiting: z.array(
    z.object({ visitId: z.uuid(), facilityName: z.string(), visitDate: z.iso.date() }),
  ),
  /** Feedback already given, newest first, with any reply. */
  given: z.array(
    z.object({
      id: z.uuid(),
      visitDate: z.iso.date(),
      facilityName: z.string(),
      rating: feedbackRatingSchema,
      comment: z.string().nullable(),
      submittedAt: z.iso.datetime(),
      reply: z.string().nullable(),
      repliedAt: z.iso.datetime().nullable(),
    }),
  ),
});
export type PortalFeedbackView = z.infer<typeof portalFeedbackSchema>;

// --- Staff ---------------------------------------------------------------------------------

export const FEEDBACK_FILTER_STATUSES = ['open', 'resolved', 'escalated', 'all'] as const;
export const FEEDBACK_RATING_BANDS = ['low', 'middle', 'high', 'all'] as const;

export const feedbackListQuerySchema = z.object({
  status: z.enum(FEEDBACK_FILTER_STATUSES).default('open'),
  rating: z.enum(FEEDBACK_RATING_BANDS).default('all'),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.uuid().optional(),
});
export type FeedbackListQuery = z.infer<typeof feedbackListQuerySchema>;

/** A list row: no comment text (it is read on the audited detail view only). */
export const feedbackSummarySchema = z.object({
  id: z.uuid(),
  submittedAt: z.iso.datetime(),
  rating: feedbackRatingSchema,
  source: z.enum(FEEDBACK_SOURCES),
  hasComment: z.boolean(),
  status: z.enum(FEEDBACK_STATUSES),
  escalated: z.boolean(),
  replied: z.boolean(),
  facilityName: z.string(),
  visitNumber: z.string(),
});
export type FeedbackSummaryView = z.infer<typeof feedbackSummarySchema>;

export const feedbackPageSchema = z.object({
  items: z.array(feedbackSummarySchema),
  nextCursor: z.uuid().nullable(),
});

export const feedbackDetailSchema = feedbackSummarySchema.extend({
  patientId: z.uuid(),
  patientName: z.string(),
  patientNumber: z.string(),
  visitId: z.uuid(),
  comment: z.string().nullable(),
  reply: z.string().nullable(),
  repliedAt: z.iso.datetime().nullable(),
  repliedBy: actorSchema.nullable(),
  resolutionNote: z.string().nullable(),
  resolvedAt: z.iso.datetime().nullable(),
  resolvedBy: actorSchema.nullable(),
  escalation: z
    .object({
      reason: z.string(),
      at: z.iso.datetime(),
      by: actorSchema,
      /** Set when the incident slice has linked an incident. */
      incidentId: z.uuid().nullable(),
    })
    .nullable(),
});
export type FeedbackDetailView = z.infer<typeof feedbackDetailSchema>;

export const feedbackTrendWeekSchema = z.object({
  /** The Monday the week starts on, East Africa Time. */
  weekStart: z.iso.date(),
  count: z.number().int().min(0),
  /** Average rating to one decimal; null with no feedback that week. */
  average: z.number().nullable(),
  /** How many rated 1 or 2. */
  low: z.number().int().min(0),
});

export const feedbackTrendsSchema = z.object({
  weeks: z.array(feedbackTrendWeekSchema),
  open: z.number().int().min(0),
  escalated: z.number().int().min(0),
});
export type FeedbackTrendsView = z.infer<typeof feedbackTrendsSchema>;

export const feedbackReplyBodySchema = z.object({ reply: z.string().trim().min(2).max(1000) });
export const feedbackResolveBodySchema = z.object({ note: z.string().trim().min(3).max(500) });
export const feedbackEscalateBodySchema = z.object({ reason: z.string().trim().min(5).max(500) });

/** POST /visits/:visitId/feedback-request. */
export const feedbackRequestResultSchema = z.object({
  requestId: z.uuid(),
  /** The last four digits of the mobile the link goes to. */
  phoneEnding: z.string(),
  expiresAt: z.iso.datetime(),
});
export type FeedbackRequestResult = z.infer<typeof feedbackRequestResultSchema>;
